import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const EmployeePortalSettings = () => {
  const [settings, setSettings] = useState({
    show_payslips: true,
    show_documents: true,
    show_impact: true,
    show_roadmap: true,
    show_surveys: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/employee-portal-menu`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/employee-portal-menu`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const menuItems = [
    { key: 'show_payslips', label: 'Payslips', description: 'Allow employees to view and download payslips' },
    { key: 'show_documents', label: 'Documents', description: 'Allow employees to access personal documents' },
    { key: 'show_impact', label: 'Our Impact to Date', description: 'Show the company impact statistics page' },
    { key: 'show_roadmap', label: '2026 Roadmap', description: 'Show the company roadmap page' },
    { key: 'show_surveys', label: 'Surveys', description: 'Allow employees to complete surveys' }
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 text-center">
        <p className="text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Employee Portal Menu</h2>
        <p className="text-sm text-gray-500">
          Control which menu items are visible to employees in their portal
        </p>
      </div>

      <div className="space-y-4 mb-6">
        {menuItems.map(item => (
          <div 
            key={item.key}
            className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {settings[item.key] ? (
                  <Eye className="w-5 h-5 text-teal-600" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                )}
                <h3 className="font-semibold text-gray-900">{item.label}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.description}</p>
            </div>
            <div className="ml-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings[item.key]}
                  onChange={() => toggleSetting(item.key)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Changes will take effect immediately for all employees. They may need to refresh their page to see the updated menu.
        </p>
      </div>
    </div>
  );
};

export default EmployeePortalSettings;
